/**
 * Caught: Elon's pickup arriving after the catch — plan-view math only (no three.js), driven by GameScene § caught.
 * It pulls out of your lane into the free neighbouring one, flicks into a drift (the tail swings out to ~79°, a quick
 * countersteer catches it at ~61°, it settles at 69°), tyres smoking, and stops beside you a little behind, nose
 * angled in. Reduced motion: a shorter, straight pull-up with a gentle angle, no swing.
 *
 * Scene axes: x across the road, z along it (+z = ahead), yaw = rotation.y (0 = nose to +z). Both vehicles are
 * boxes in plan view (height ignored, so a car up on a roof or in the air is kept clear too); every pose keeps
 * CLEARANCE between them — `keepClear()` is the guard, used by the run's chase placement as well.
 */

export interface Pose2 { x: number; z: number; yaw: number; }
export interface Box2 extends Pose2 { halfWidth: number; halfLength: number; }
/** an obstacle's footprint (scene coordinates) */
export interface Area { x0: number; x1: number; z0: number; z1: number; }
export interface CaughtFrame extends Pose2 {
  /** body roll (rotation.z) and the lift that keeps the low side's wheels on the road */
  roll: number;
  lift: number;
  /** front-wheel steer, relative to the body (rotation.y of the front wheels) */
  steer: number;
  /** 0–1: how hard the tyres smoke */
  smoke: number;
}

/** the roadster: 4.4 m + the jets' lips, 2.06 m across */
export const CAR_HALF = { halfWidth: 1.05, halfLength: 2.3 } as const;
/** the pickup: 5.24 m over the bumpers, 2.2 m over the bumpers and mirror stubs */
export const COP_HALF = { halfWidth: 1.1, halfLength: 2.62 } as const;
/** the least gap between the two footprints in any frame */
export const CLEARANCE = 0.4;
/** the gap it stops at: close enough to read as boxed in */
export const REST_GAP = 0.6;
/** where it stops: its centre this far behind the car's, nose turned in this far */
export const REST_BEHIND = 2.0;
export const REST_YAW = 1.2;            // 69°
const REDUCED_YAW = 0.25;               // 14°, reached without any swing
const SLIDE_YAW = 1.38;                 // 79°: the tail swings out this far …
const CATCH_YAW = 1.06;                 // … the countersteer catches it (61°) …
const SETTLE_YAW = 1.25;                // … a last small rock, then REST_YAW
const KICK_YAW = 0.14;                  // the pull-out into the next lane, before the flick
/** the car's own spin after the hit: its nose turns toward the pickup's side (the tail away from where it stops) */
export const CAR_YAW = 0.5;
/** seconds: pulling out, the tail fully out, stopped; reduced motion stops at STOP_REDUCED */
export const TIMES = { pull: 0.3, slide: 0.62, stop: 1.0 } as const;
export const STOP_REDUCED = 0.6;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const easeOut = (u: number) => 1 - (1 - clamp01(u)) ** 3;
const smooth = (u: number) => { const k = clamp01(u); return k * k * (3 - 2 * k); };

// ------------------------------------------------------------------------------------------------ boxes

const scratchA = new Float64Array(8), scratchB = new Float64Array(8);

/** the four corners, rotation.y style: local (lx, lz) → (x + lx·cos + lz·sin, z − lx·sin + lz·cos) */
const SIGNS = [1, 1, -1, 1, -1, -1, 1, -1];
function corners(b: Box2, out: Float64Array): Float64Array {
  const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
  const signs = SIGNS;
  for (let i = 0; i < 4; i++) {
    const lx = signs[i * 2] * b.halfWidth, lz = signs[i * 2 + 1] * b.halfLength;
    out[i * 2] = b.x + lx * c + lz * s;
    out[i * 2 + 1] = b.z - lx * s + lz * c;
  }
  return out;
}

function pointToSegment(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax, dz = bz - az;
  const k = clamp01(((px - ax) * dx + (pz - az) * dz) / (dx * dx + dz * dz || 1));
  return Math.hypot(px - (ax + dx * k), pz - (az + dz * k));
}

/** The gap between two boxes in plan view: > 0 apart (the least distance), ≤ 0 overlapping (minus the least overlap). */
export function clearance(a: Box2, b: Box2): number {
  const ca = corners(a, scratchA), cb = corners(b, scratchB);
  let depth = Infinity;
  for (let axis = 0; axis < 4; axis++) {                           // each box's two edge directions (SAT)
    const yaw = axis < 2 ? a.yaw : b.yaw;
    const c = Math.cos(yaw), s = Math.sin(yaw);
    const ax = axis % 2 === 0 ? c : s, az = axis % 2 === 0 ? -s : c;
    let a0 = Infinity, a1 = -Infinity, b0 = Infinity, b1 = -Infinity;
    for (let i = 0; i < 4; i++) {
      const pa = ca[i * 2] * ax + ca[i * 2 + 1] * az, pb = cb[i * 2] * ax + cb[i * 2 + 1] * az;
      a0 = Math.min(a0, pa); a1 = Math.max(a1, pa); b0 = Math.min(b0, pb); b1 = Math.max(b1, pb);
    }
    const overlap = Math.min(a1, b1) - Math.max(a0, b0);
    if (overlap <= 0) { depth = -1; break; }                       // a separating axis: they are apart
    depth = Math.min(depth, overlap);
  }
  if (depth > 0) return -depth;
  let best = Infinity;                                             // apart: the least corner-to-edge distance
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    for (let k = 0; k < 4; k++) {
      best = Math.min(best,
        pointToSegment(ca[k * 2], ca[k * 2 + 1], cb[i * 2], cb[i * 2 + 1], cb[j * 2], cb[j * 2 + 1]),
        pointToSegment(cb[k * 2], cb[k * 2 + 1], ca[i * 2], ca[i * 2 + 1], ca[j * 2], ca[j * 2 + 1]));
    }
  }
  return best;
}

export const copBox = (p: Pose2): Box2 => ({ x: p.x, z: p.z, yaw: p.yaw, ...COP_HALF });
export const carBox = (p: Pose2): Box2 => ({ x: p.x, z: p.z, yaw: p.yaw, ...CAR_HALF });

/**
 * The guard: the pickup's pose moved straight away from the car (centre to centre; straight back when they share a
 * centre) just far enough to keep `margin` between them. Unchanged when it is clear already.
 */
export function keepClear(cop: Pose2, car: Pose2, margin = CLEARANCE): Pose2 {
  const carB = carBox(car);
  let dx = cop.x - car.x, dz = cop.z - car.z;
  const length = Math.hypot(dx, dz);
  if (length < 1e-6) { dx = 0; dz = -1; } else { dx /= length; dz /= length; }
  const gapAt = (k: number) => clearance(copBox({ x: cop.x + dx * k, z: cop.z + dz * k, yaw: cop.yaw }), carB);
  if (gapAt(0) >= margin) return cop;
  let lo = 0, hi = 0.25;
  while (gapAt(hi) < margin && hi < 64) { lo = hi; hi *= 2; }
  for (let i = 0; i < 16; i++) { const mid = (lo + hi) / 2; if (gapAt(mid) >= margin) hi = mid; else lo = mid; }
  return { x: cop.x + dx * hi, z: cop.z + dz * hi, yaw: cop.yaw };
}

// ------------------------------------------------------------------------------------------------ the stop

/**
 * Where it stops: on `side` (+1 = +x), REST_BEHIND behind the car's centre, nose angled in, REST_GAP clear of the car
 * as it ends up (`car.yaw` = its spin after the hit). Slid out along x until the gap is right (convex boxes: once
 * apart, the gap only grows outward).
 */
export function restPose(car: Pose2, side: 1 | -1, reduced = false): Pose2 {
  const yaw = -side * (reduced ? REDUCED_YAW : REST_YAW);
  const z = car.z - REST_BEHIND;
  const carB = carBox(car);
  let lo = 0, hi = 10;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (clearance(copBox({ x: car.x + side * mid, z, yaw }), carB) >= REST_GAP) hi = mid; else lo = mid;
  }
  return { x: car.x + side * hi, z, yaw };
}

/** half the pickup's extent across the road at this yaw */
export const copHalfAcross = (yaw: number) => COP_HALF.halfLength * Math.abs(Math.sin(yaw)) + COP_HALF.halfWidth * Math.abs(Math.cos(yaw));

/**
 * Which side it pulls up on (+1 = +x): a side where the stopped pickup fits on the road (a little over the curb is
 * fine) beats one where it doesn't, a side free of obstacles along its way beats a blocked one, then the side with
 * clearly more room; a tie (the middle lane, both sides free) takes `alternate`.
 */
export function pickSide(car: Pose2, roadHalf: number, obstacles: Area[], alternate: 1 | -1, reduced = false): 1 | -1 {
  const score = (side: 1 | -1) => {
    const rest = restPose({ x: car.x, z: car.z, yaw: side * CAR_YAW }, side, reduced);
    const outer = Math.abs(rest.x - car.x) + copHalfAcross(rest.yaw);
    const room = roadHalf - side * car.x;
    const near = car.x + side * (CAR_HALF.halfWidth + 0.2), far = car.x + side * outer;
    const lane: Area = { x0: Math.min(near, far), x1: Math.max(near, far), z0: car.z - 10, z1: rest.z + 3.5 };
    const blocked = obstacles.some((o) => o.x0 < lane.x1 && o.x1 > lane.x0 && o.z0 < lane.z1 && o.z1 > lane.z0);
    return { value: (room >= outer - 0.8 ? 2 : 0) + (blocked ? 0 : 1), room };
  };
  const plus = score(1), minus = score(-1);
  if (plus.value !== minus.value) return plus.value > minus.value ? 1 : -1;
  if (Math.abs(plus.room - minus.room) > 1.5) return plus.room > minus.room ? 1 : -1;
  return alternate;
}

// ------------------------------------------------------------------------------------------------ the drift

/** linear keyframes [t, value] */
function keys(t: number, frames: readonly (readonly [number, number])[]): number {
  if (t <= frames[0][0]) return frames[0][1];
  for (let i = 1; i < frames.length; i++) {
    const [t1, v1] = frames[i];
    if (t <= t1) {
      const [t0, v0] = frames[i - 1];
      const k = (t - t0) / (t1 - t0);
      return v0 + (v1 - v0) * (0.5 - Math.cos(Math.PI * k) / 2);   // eased between keys
    }
  }
  return frames[frames.length - 1][1];
}

/**
 * The pickup at `t` seconds after the catch, from where it was (`from`, its pose at the catch) to `rest`
 * (restPose). Lateral first (an ease-out), forward later (a smoothstep), both land on the stop with zero speed.
 * Not yet guarded: the caller runs keepClear() on it against the car's pose of that frame.
 */
export function caughtPose(t: number, from: Pose2, rest: Pose2, side: 1 | -1, reduced = false): CaughtFrame {
  const stop = reduced ? STOP_REDUCED : TIMES.stop;
  const u = t / stop;
  const x = from.x + (rest.x - from.x) * easeOut(u);
  const z = from.z + (rest.z - from.z) * smooth(u);
  if (reduced) {
    const yaw = from.yaw + (rest.yaw - from.yaw) * smooth(u);
    return { x, z, yaw, roll: 0, lift: 0, steer: side * 0.2 * (1 - smooth(u)), smoke: 0 };
  }
  const { pull, slide } = TIMES;
  // yaw, in the side's sense: out into the lane, the flick (tail out), the countersteer catch, a last rock, rest
  let turn: number;
  if (t < pull) turn = KICK_YAW * Math.sin(Math.PI * t / pull);
  else if (t < slide) turn = -SLIDE_YAW * easeOut((t - pull) / (slide - pull));
  else turn = keys(t, [[slide, -SLIDE_YAW], [slide + 0.14, -CATCH_YAW], [slide + 0.27, -SETTLE_YAW], [stop, -REST_YAW]]);
  const fade = Math.max(0, 1 - t / pull) ** 2;                     // whatever yaw it had at the catch fades out
  const yaw = side * turn + from.yaw * fade;
  // front wheels: into the lane, the flick in, full countersteer while the tail is out, left turned at the stop
  const steer = side * keys(t, [[0, 0.3], [pull * 0.6, 0.3], [pull, -0.45], [pull + 0.12, 0.55], [slide + 0.14, 0.4], [stop, 0.22]]);
  // body roll: leans toward where it slides (the road ahead) and rocks back once at the stop
  const lean = t < stop ? Math.sin(Math.PI * clamp01((t - pull) / (stop - pull))) * 0.06
    : -Math.sin(Math.PI * clamp01((t - stop) / 0.28)) * 0.025;
  const roll = -side * lean;
  const smoke = t < pull * 0.8 ? 0 : t < stop ? 1 : clamp01(1 - (t - stop) / 0.2);
  return { x, z, yaw, roll, lift: Math.abs(Math.sin(roll)) * 1.0, steer, smoke };
}
